import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useSearchParams, Link } from "react-router-dom";
import { SubscriptionPlansTable } from "@/components/SubscriptionPlansTable";
import { useSeo } from "@/hooks/useSeo";
import { useStructuredData } from "@/hooks/useStructuredData";
import { getReferralDisplayState } from "@/lib/referrals";

const SIGNATURE_ADVISOR_BREAKDOWN = [
  "Guided District Discovery",
  "2026 ABSD/BSD Calculator",
  "Rent vs. Buy Analysis",
  "Docked Side-by-Side Viewing",
  "Intelligent Alternatives",
  "Structured Comparisons",
  "Gated PDF Report Delivery",
  "Integrated Viewing Requests",
  "Contextual Listing Support",
  "Direct WhatsApp Handoff",
  "Adaptive Memory Profile",
  "AI Comparison Strategist",
  "Shortlist Curation",
  "Investor Thesis Engine",
  "District Intelligence Narratives",
  "Objection-Handling Intelligence",
  "Investment Memo Generation",
  "Scenario Stress-Testing",
  "Concierge-Ready Briefing",
];

const SOVEREIGNTY_PILLARS = [
  "Stronger Personal Brand",
  "Exclusive Signals",
  "Institutional Conversion",
  "Escape PPL Dependency",
  "Sovereign Traffic",
  "Positioning Control",
  "Direct Signal Quality",
  "Built-in Conversion Funnels",
  "SEO Legacy",
  "Operational Efficiency",
  "Centralized System",
  "Elite Client Trust",
];

const PRICING_FAQ_ITEMS = [
  {
    question: "What plans are available on SG Prop Portal?",
    answer:
      "SG Prop Portal currently offers three public plans: Digital Agent (Basic) at S$129/month, Alpha Investor (Pro) at S$249/month, and Elite Team (Enterprise Plan) at S$899/month.",
  },
  {
    question: "What makes the monthly fee usable value instead of just another website cost?",
    answer:
      "The pricing is justified through hard utility: the Signature Advisor feature stack, lead sovereignty, AI-assisted conversion flows, and the operating-system layer that helps agents own traffic instead of renting attention.",
  },
  {
    question: "What is included in the Signature Advisor stack?",
    answer:
      "The page highlights a 19-feature Signature Advisor system, including district discovery, ROI analysis, side-by-side comparisons, PDF report delivery, objection-handling intelligence, investment memo generation, and concierge-ready briefings.",
  },
  {
    question: "Why does SG Prop Portal talk about sovereignty on the pricing page?",
    answer:
      "Because the platform is positioned as an ownership layer. The sovereignty pillars explain how the product supports stronger brand control, direct signal quality, built-in conversion funnels, SEO legacy, and elite client trust.",
  },
];

const PRICING_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: PRICING_FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

export const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const referralState = getReferralDisplayState(searchParams.get("ref"));
  const referralCode = referralState.activeCode;

  useSeo({
    title: "Pricing | SG Prop Portal",
    description: "See SG Prop Portal pricing, the Signature Advisor feature stack, and the sovereignty utility that justifies the monthly investment.",
  });

  useStructuredData("pricing-faq-schema", PRICING_FAQ_SCHEMA);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#fbf6e7_0%,#f7efdf_46%,#e4ebf2_100%)] text-[#001935]" data-testid="pricing-page">
      <section className="section-shell pt-10" data-testid="pricing-hero-section">
        <div className="rounded-[36px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#07192f_0%,#102947_40%,#1b395a_100%)] px-8 py-10 text-[#F9F8F6] shadow-[0_28px_80px_rgba(0,25,53,0.22)] lg:px-10 lg:py-12">
          <div className="grid gap-8 xl:grid-cols-[1.12fr_0.88fr] xl:items-end">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]" data-testid="pricing-hero-kicker">Pricing</p>
              {referralCode ? (
                <div className="mt-4 inline-flex items-center rounded-full border border-[#D4AF37]/25 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#F7E6B4]" data-testid="pricing-referral-banner">
                  {referralState.isStoredWithoutQuery ? `Browser locked • ${referralCode}` : `Referral locked • ${referralCode}`} • first-click attribution for 60 days
                </div>
              ) : null}
              <h1 className="mt-4 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-white sm:text-5xl" data-testid="pricing-hero-heading">
                Choose the plan that closes the utility gap.
              </h1>
              <p className="mt-5 text-sm leading-8 text-[#E6DCC4] sm:text-base" data-testid="pricing-hero-copy">
                Reuse the exact SG Prop Portal plan structure already shown on the main landing page, then justify the monthly investment through hard utility: Signature Advisor depth, lead sovereignty, and white-label conversion control.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link className="gold-button" data-testid="pricing-hero-primary-cta" to={`/signup?plan=pro${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ""}`}>
                  Secure Signature Access <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <a className="outline-button border-white/18 text-white hover:bg-white/8" data-testid="pricing-hero-secondary-cta" href="#subscription-plans-section">
                  Compare Plans
                </a>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-1" data-testid="pricing-hero-metric-grid">
              {[
                { label: "Plan 01", value: "S$129/month", detail: "Digital Agent (Basic)" },
                { label: "Plan 02", value: "S$249/month", detail: "Alpha Investor (Pro)" },
                { label: "Plan 03", value: "S$899/month", detail: "Elite Team (Enterprise Plan)" },
              ].map((card) => (
                <div className="rounded-[24px] border border-[#D4AF37]/18 bg-white/8 px-5 py-5 backdrop-blur-sm xl:min-h-[154px]" data-testid={`pricing-hero-metric-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={card.label}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">{card.label}</p>
                  <p className="mt-3 text-[2rem] font-semibold leading-none tracking-tight text-white xl:text-[2.15rem]">{card.value}</p>
                  <p className="mt-3 text-base leading-7 text-[#E6DCC4]">{card.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-6">
        <SubscriptionPlansTable />
      </section>

      <section className="section-shell py-6" data-testid="pricing-signature-advisor-breakdown-section">
        <div className="rounded-[36px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 shadow-[0_32px_90px_rgba(0,25,53,0.12)] lg:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Signature Advisor breakdown</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="pricing-signature-advisor-heading">19 reasons the monthly fee becomes usable value.</h2>
            <p className="mt-4 text-sm leading-8 text-[#42566f] md:text-base" data-testid="pricing-signature-advisor-copy">
              This is the actual feature stack buyers and agents interact with every day. It turns a brochure-level portal into a decision engine, which is exactly what closes the gap between a basic website and a paid advisory system.
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="pricing-signature-advisor-grid">
            {SIGNATURE_ADVISOR_BREAKDOWN.map((feature, index) => (
              <div className="rounded-[24px] border border-[#001935]/10 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(0,25,53,0.05)]" data-testid={`pricing-signature-advisor-feature-${index + 1}`} key={feature}>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Feature {String(index + 1).padStart(2, "0")}</p>
                <p className="mt-3 text-base font-semibold text-[#001935]">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell py-6" data-testid="pricing-sovereignty-pillars-section">
        <div className="rounded-[36px] bg-[linear-gradient(135deg,rgba(212,175,55,0.28)_0%,rgba(212,175,55,0.08)_38%,rgba(255,255,255,0.06)_100%)] p-[1px] shadow-[0_22px_60px_rgba(0,0,0,0.18)]">
          <div className="rounded-[35px] border border-white/6 bg-[linear-gradient(135deg,rgba(3,26,52,0.88)_0%,rgba(13,35,61,0.84)_100%)] p-8 lg:p-10" data-testid="pricing-sovereignty-panel">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">Pillars of sovereignty</p>
              <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#F9F8F6] md:text-5xl" data-testid="pricing-sovereignty-heading">The ownership layer that justifies recurring spend.</h2>
              <p className="mt-4 text-sm leading-8 text-[#E6DCC4] md:text-base" data-testid="pricing-sovereignty-copy">
                These pillars turn SG Prop Portal from a rented marketing expense into a controlled operating system. This is where the monthly investment compounds instead of disappearing into third-party lead dependency.
              </p>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="pricing-sovereignty-grid">
              {SOVEREIGNTY_PILLARS.map((pillar, index) => (
                <div className="rounded-[24px] border border-[#D4AF37]/14 bg-[rgba(255,255,255,0.05)] px-5 py-5" data-testid={`pricing-sovereignty-pillar-${index + 1}`} key={pillar}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/20 bg-[rgba(212,175,55,0.14)] text-[#D4AF37]">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Pillar {String(index + 1).padStart(2, "0")}</p>
                      <p className="mt-2 text-base leading-8 text-[#F8F3E8] md:text-[1.05rem]">{pillar}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-[24px] border border-[#D4AF37]/14 bg-[rgba(255,255,255,0.04)] px-5 py-5" data-testid="pricing-sovereignty-hook">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Decision lens</p>
                  <p className="mt-2 text-sm leading-7 text-[#F8F3E8]">At S$129/mo and above, the conversation is no longer “Is this just a website?” It is whether you want to keep renting attention or start owning the operating layer that converts it.</p>
                </div>
                <Link className="gold-button" data-testid="pricing-sovereignty-cta" to="/signup?plan=pro">
                  Claim Signature Selection <Sparkles className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-6" data-testid="pricing-faq-section">
        <div className="rounded-[36px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 shadow-[0_32px_90px_rgba(0,25,53,0.12)] lg:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="pricing-faq-kicker">Pricing FAQ</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="pricing-faq-heading">
              The key questions behind the monthly decision.
            </h2>
            <p className="mt-4 text-sm leading-8 text-[#42566f] md:text-base" data-testid="pricing-faq-copy">
              These answers mirror the value case already presented on this page, making the pricing conversation clearer for both buyers and search engines.
            </p>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-2" data-testid="pricing-faq-grid">
            {PRICING_FAQ_ITEMS.map((item, index) => (
              <div className="rounded-[24px] border border-[#001935]/10 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(0,25,53,0.05)]" data-testid={`pricing-faq-item-${index + 1}`} key={item.question}>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`pricing-faq-label-${index + 1}`}>
                  Question {String(index + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-3 text-xl font-semibold leading-tight text-[#001935]" data-testid={`pricing-faq-question-${index + 1}`}>
                  {item.question}
                </h3>
                <p className="mt-3 text-sm leading-8 text-[#42566f] md:text-base" data-testid={`pricing-faq-answer-${index + 1}`}>
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell pb-14 pt-6" data-testid="pricing-final-cta-section">
        <div className="rounded-[34px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 text-center shadow-[0_28px_80px_rgba(0,25,53,0.1)]">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Signature Selection</p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl">Choose the plan that matches your closing ambition.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-8 text-[#42566f] md:text-base">This page exists to make the investment case clear: pricing, utility, sovereignty, and the exact feature depth your buyers and advisors will actually use.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link className="gold-button" data-testid="pricing-final-primary-cta" to="/signup?plan=pro">
              Start with Alpha Investor <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <a className="outline-button border-[#001935]/14 text-[#001935] hover:bg-[#001935]/5" data-testid="pricing-final-secondary-cta" href="#subscription-plans-section">
              Review Plan Table
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};